import { Component, createSignal, Show } from "solid-js";
import { Modal } from "../ui";
import { TypeIcon } from "../work-items/TypeIcon";
import { QuickCreateCard } from "./QuickCreateCard";
import type {
  WorkItemType,
  WorkItemStatusId,
  CreateWorkItemRequest,
  ProjectId,
} from "../../api";

export interface QuickCreateModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Project ID for AI enrichment context */
  projectId: ProjectId;
  /** Status ID to assign to the new item */
  statusId: WorkItemStatusId;
  /** Column name for display in modal title */
  columnName?: string;
  /** Epic title when an epic filter is active (item will auto-assign to this epic) */
  epicTitle?: string;
  /** Available work item types to choose from */
  itemTypes: WorkItemType[];
  /** Loading state while creating */
  loading?: boolean;
  /** Callback when create is submitted */
  onCreate: (data: CreateWorkItemRequest) => void;
}

/**
 * QuickCreateModal - Modal wrapper for QuickCreateCard
 *
 * Moves the quick create form to a modal overlay to prevent layout shift
 * issues in the board column when AI enrichment data appears.
 *
 * Features:
 * - Wraps QuickCreateCard in Modal component
 * - Fixed position relative to viewport (no layout shift)
 * - "Create another" functionality for batch creation
 * - Handles Escape key to close (via Modal)
 * - Coordinates with QuickCreateCard's internal Escape handling
 */
export const QuickCreateModal: Component<QuickCreateModalProps> = (props) => {
  const [createAnother, setCreateAnother] = createSignal(false);

  const modalTitle = () =>
    props.columnName ? `Create item in ${props.columnName}` : "Create item";

  const handleCreate = (data: CreateWorkItemRequest) => {
    props.onCreate(data);
    // If not creating another, the parent will close the modal
    // If creating another, the modal stays open
  };

  const handleCancel = () => {
    setCreateAnother(false);
    props.onClose();
  };

  return (
    <Modal
      open={props.open}
      onClose={handleCancel}
      title={modalTitle()}
      size="lg"
      position="top"
      closeOnEscape={true}
      closeOnBackdrop={true}
    >
      {/* Epic context indicator when creating within an epic scope */}
      <Show when={props.epicTitle}>
        {(title) => (
          <div class="mb-3 flex items-center gap-1.5 text-xs text-ink-2 bg-sunken border border-line rounded px-2.5 py-1.5">
            <TypeIcon typeName="Epic" />
            <span>
              Adding to <span class="font-medium">{title()}</span>
            </span>
          </div>
        )}
      </Show>

      <QuickCreateCard
        projectId={props.projectId}
        statusId={props.statusId}
        itemTypes={props.itemTypes}
        loading={props.loading}
        onCreate={handleCreate}
        onCancel={handleCancel}
      />

      {/* Create another checkbox */}
      <div class="mt-4 pt-3 border-t border-line">
        <label class="flex items-center gap-2 text-sm text-ink-2 cursor-pointer">
          <input
            type="checkbox"
            checked={createAnother()}
            onChange={(e) => setCreateAnother(e.currentTarget.checked)}
            class="w-4 h-4 rounded-sm border-line-strong accent-ink focus-ring"
          />
          Create another after this one
        </label>
      </div>
    </Modal>
  );
};

export default QuickCreateModal;
